import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';
import { useMultiHOA } from './MultiHOAContext';
import api from './api';
import { 
  PlusIcon, 
  TrashIcon, 
  UsersIcon,
  UserPlusIcon,
  UserMinusIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const CommitteeManagement = () => {
  const { user } = useContext(AuthContext);
  const { activeHoaId } = useMultiHOA();
  const [committees, setCommittees] = useState([]);
  const [boardMembers, setBoardMembers] = useState([]);

  const [homeowners, setHomeowners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [newCommittee, setNewCommittee] = useState({
    name: '',
    description: '',
    type: 'standing'
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Get branding colors from CSS variables
  const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color') || '#4f46e5';

  useEffect(() => {
    fetchBoardMembers();

    fetchCommittees();
    fetchHomeowners();
  }, [user]);

  const fetchCommittees = async () => {
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      const { data } = await api.get(`/api/hoa/${currentHoaId}/committees`);
      setCommittees(data.committees || []);
    } catch (error) {
      console.error('Error fetching committees:', error);
      setCommittees([]);
    }
  };

  const fetchHomeowners = async () => {
    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      const { data } = await api.get(`/api/users/homeowners?hoa_id=${currentHoaId}`);
      setHomeowners(data.homeowners || []);
    } catch (error) {
      console.error('Error fetching homeowners:', error);
      setHomeowners([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBoardMembers = async () => {
    try {
      const { data } = await api.get('/api/board/members');
      setBoardMembers(data.board_members || []);
    } catch (error) {
      console.error('Error fetching board members:', error);
      setBoardMembers([]);
    }
  };

  const createCommittee = async () => {
    if (!newCommittee.name.trim()) {
      alert('Committee name is required');
      return;
    }

    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      const { data } = await api.post(`/api/hoa/${currentHoaId}/committees`, {
        ...newCommittee,
        hoa_id: currentHoaId,
        created_by: user.id
      });

      if (data.success) {
        alert('Committee created successfully!');
        setShowCreateModal(false);
        setNewCommittee({ name: '', description: '', type: 'standing' });
        fetchCommittees();
      }
    } catch (error) {
      console.error('Error creating committee:', error);
      alert('Failed to create committee');
    }
  };

  const deleteCommittee = async (committeeId) => {
    if (!window.confirm('Are you sure you want to delete this committee? This action cannot be undone.')) {
      return;
    }

    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      await api.delete(`/api/hoa/${currentHoaId}/committees/${committeeId}`);
      alert('Committee deleted successfully');
      fetchCommittees();
    } catch (error) {
      console.error('Error deleting committee:', error);
      alert('Failed to delete committee');
    }
  };

  const addMemberToCommittee = async (homeownerId) => {
    if (!selectedCommittee) return;

    // Prevent duplicate additions by checking if already exists
    const existingMember = selectedCommittee.members?.find(m => (m.user_id || m.id) === homeownerId);
    if (existingMember) {
      alert('❌ This member is already in the committee!');
      return;
    }

    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      await api.post(`/api/hoa/${currentHoaId}/committees/${selectedCommittee.id}/members`, {
        user_id: homeownerId
      });
      
      alert('✅ Member added to committee successfully!');
      // Refresh data to update the UI immediately
      await fetchCommittees();
      
      // Find and update the selected committee with fresh data
      const { data: updatedCommittees } = await api.get(`/api/hoa/${currentHoaId}/committees`);
      const updatedCommittee = updatedCommittees.committees?.find(c => c.id === selectedCommittee.id);
      if (updatedCommittee) {
        setSelectedCommittee(updatedCommittee);
      }
      
      // Trigger committee chat reconciliation to ensure new member is added to all committee chats
      try {
        await api.post(`/api/chat/reconcile-committee-chats?hoa_id=${currentHoaId}&committee_id=${selectedCommittee.id}`, {});
        console.log('✅ Committee chats reconciled for new member');
      } catch (reconcileError) {
        console.warn('⚠️ Failed to reconcile committee chats:', reconcileError);
        // Don't show error to user as the main action succeeded
      }
      
    } catch (error) {
      console.error('Error adding member:', error);
      if (error.response?.status === 409) {
        alert('❌ This member is already in the committee!');
      } else {
        alert('❌ Failed to add member to committee');
      }
    }
  };

  // Combined candidates (homeowners + board members) for committee membership
  const combinedCandidates = useMemo(() => {
    // Merge homeowners and board members, dedupe by id
    const byId = new Map();
    homeowners.forEach(h => byId.set(h.id, { ...h, _source: 'homeowner' }));
    boardMembers.forEach(b => {
      if (!byId.has(b.id)) byId.set(b.id, { ...b, _source: 'board' });
    });
    const arr = Array.from(byId.values());
    // Exclude those already in committee
    const currentIds = new Set((selectedCommittee?.members || []).map(m => m.user_id || m.id));
    return arr.filter(u => !currentIds.has(u.id));
  }, [homeowners, boardMembers, selectedCommittee]);

  const filteredCandidates = useMemo(() => {
    const q = (searchTerm || '').toLowerCase();
    if (!q) return combinedCandidates;
    return combinedCandidates.filter(u => (
      (u.first_name || '').toLowerCase().includes(q) ||
      (u.last_name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.address || u.property_address || '').toLowerCase().includes(q)
    ));
  }, [combinedCandidates, searchTerm]);

  const removeMemberFromCommittee = async (homeownerId) => {
    if (!selectedCommittee) return;

    try {
      const currentHoaId = activeHoaId || user?.hoa_id;
      if (!currentHoaId) return;
      
      await api.delete(`/api/hoa/${currentHoaId}/committees/${selectedCommittee.id}/members/${homeownerId}`);
      
      alert('Member removed from committee successfully!');
      fetchCommittees();
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member from committee');
    }
  };

  const getAvailableHomeowners = () => {
    if (!selectedCommittee) return [];
    const currentMemberIds = selectedCommittee.members?.map(m => m.user_id || m.id) || [];
    return homeowners.filter(h => !currentMemberIds.includes(h.id));
  };

  // Filter homeowners based on search term
  const filteredHomeowners = homeowners.filter(homeowner => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      homeowner.first_name?.toLowerCase().includes(searchLower) ||
      homeowner.last_name?.toLowerCase().includes(searchLower) ||
      homeowner.email?.toLowerCase().includes(searchLower) ||
      homeowner.address?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{borderColor: primaryColor}}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Committee Management</h3>
          <p className="text-sm text-gray-600">Manage committees and their members</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity flex items-center"
          style={{backgroundColor: primaryColor}}
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Committee
        </button>
      </div>

      {/* Committee List */}
      <div className="grid gap-4">
        {committees.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Committees</h3>
            <p className="text-gray-600 mb-4">Create your first committee to get started</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 text-white rounded-lg"
              style={{backgroundColor: primaryColor}}
            >
              Create Committee
            </button>
          </div>
        ) : (
          committees.map((committee) => (
            <div key={committee.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <UsersIcon className="h-5 w-5 mr-2" style={{color: primaryColor}} />
                    <h4 className="text-lg font-medium text-gray-900">{committee.name}</h4>
                    <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                      {committee.type}
                    </span>
                  </div>
                  {committee.description && (
                    <p className="text-sm text-gray-600 mb-4">{committee.description}</p>
                  )}
                  
                  {/* Members */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        Members ({committee.members?.length || 0})
                      </span>
                      <button
                        onClick={() => {
                          setSelectedCommittee(committee);
                          setShowMemberModal(true);
                        }}
                        className="text-xs px-2 py-1 rounded"
                        style={{backgroundColor: primaryColor, color: 'white'}}
                      >
                        <UserPlusIcon className="h-3 w-3 inline mr-1" />
                        Manage Members
                      </button>
                    </div>
                    
                    {committee.members && committee.members.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {committee.members.map((member) => (
                          <div key={member.user_id || member.id} className="flex items-center bg-gray-100 rounded-full px-3 py-1">
                            <span className="text-sm text-gray-700">
                              {member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email}
                            </span>
                            <button
                              onClick={() => removeMemberFromCommittee(member.user_id || member.id)}
                              className="ml-2 text-red-500 hover:text-red-700"
                              title="Remove from committee"
                            >
                              <UserMinusIcon className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No members assigned</p>
                    )}
                  </div>
                </div>
                
                <button
                  onClick={() => deleteCommittee(committee.id)}
                  className="text-red-500 hover:text-red-700 p-1"
                  title="Delete Committee"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Committee Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Committee</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Committee Name</label>
                  <input
                    type="text"
                    value={newCommittee.name}
                    onChange={(e) => setNewCommittee({...newCommittee, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                    style={{focusRingColor: primaryColor}}
                    placeholder="Enter committee name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newCommittee.description}
                    onChange={(e) => setNewCommittee({...newCommittee, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                    rows="3"
                    placeholder="Committee description..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={newCommittee.type}
                    onChange={(e) => setNewCommittee({...newCommittee, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:border-transparent"
                  >
                    <option value="standing">Standing Committee</option>
                    <option value="ad_hoc">Ad Hoc Committee</option>
                    <option value="special">Special Committee</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewCommittee({ name: '', description: '', type: 'standing' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={createCommittee}
                  className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90"
                  style={{backgroundColor: primaryColor}}
                >
                  Create Committee
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Member Management Modal */}
      {showMemberModal && selectedCommittee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Manage Members - {selectedCommittee.name}
              </h3>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-6">
                {/* Current Members */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Current Members ({selectedCommittee.members?.length || 0})
                  </h4>
                  {selectedCommittee.members && selectedCommittee.members.length > 0 ? (
                    <div className="space-y-2">
                      {selectedCommittee.members.map((member) => (
                        <div key={member.user_id || member.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                          <div>
                            <span className="font-medium text-gray-900">
                              {member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email}
                            </span>
                            <span className="text-sm text-gray-500 ml-2">({member.role})</span>
                          </div>
                          <button
                            onClick={() => removeMemberFromCommittee(member.user_id || member.id)}
                            className="text-red-600 hover:text-red-800 text-sm px-2 py-1"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No members assigned</p>
                  )}
                </div>

                {/* Available Members (Board + Homeowners) */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Available Members</h4>
                  
                  {/* Search Input */}
                  <div className="mb-4 relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by name, email, or address..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {filteredCandidates.map((cand) => (
                      <div key={cand.id} className="flex items-center justify-between bg-white border border-gray-200 p-3 rounded-lg">
                        <div>
                          <span className="font-medium text-gray-900">
                            {cand.first_name} {cand.last_name}
                          </span>
                          <span className="text-xs ml-2 px-2 py-0.5 rounded-full border capitalize">
                            {cand._source === 'board' ? 'Board' : 'Homeowner'}
                          </span>
                          <span className="text-sm text-gray-500 ml-2">({cand.role})</span>
                        </div>
                        <button
                          onClick={() => addMemberToCommittee(cand.id)}
                          className="text-white text-sm px-3 py-1 rounded hover:opacity-90"
                          style={{backgroundColor: primaryColor}}
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowMemberModal(false);
                  setSelectedCommittee(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommitteeManagement;